import type { DateRange } from 'react-day-picker';

/** Format a local calendar date without shifting it through UTC. */
export const toReportingDateParam = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Parse YYYY-MM-DD strictly; JavaScript's rollover dates are rejected. */
export const parseReportingDateParam = (value: string | null): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!match) return undefined;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : undefined;
};

/** Keep a one-ended range while the user is choosing its second date. */
export const parseReportingDateRange = (
  fromValue: string | null,
  toValue: string | null,
): DateRange | undefined => {
  const from = parseReportingDateParam(fromValue);
  const to = parseReportingDateParam(toValue);
  if (!from && !to) return undefined;
  if (from && to && to < from) return undefined;
  return { from, to };
};

/** The Reports views, in tab order: the `?tab=` values the page accepts. */
export const REPORTING_VIEWS = [
  'overview',
  'performance',
  'consumption',
  '3pl-usage',
  'costs',
  'on-order',
  'unmatched',
] as const;
export type ReportingView = (typeof REPORTING_VIEWS)[number];

/**
 * The view to show for a `?tab=` value. A pure client (an org whose orders a
 * 3PL ships and that runs no floor of its own) gets only its 3PL usage bill,
 * whatever the URL says: every other view reports on the org's own stock and
 * floor. Anyone else gets the tab asked for, or Overview for a missing or
 * unknown one.
 */
export const resolveReportingView = (tab: string | null, isPureClient: boolean): ReportingView => {
  if (isPureClient) return '3pl-usage';
  return (REPORTING_VIEWS as readonly string[]).includes(tab ?? '') ? (tab as ReportingView) : 'overview';
};
